/* eslint-disable max-lines */
import { Button } from '@hakimo-ui/shared/ui-base';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Stage } from 'react-konva';
import {
  CanvasDimensions,
  Point,
  SpecialZoneType,
  ZonePolygonData,
} from './types';
import { useImageLoader } from './useImageLoader';
import { ImageResolution, rescaleZones } from './utils';
import { ZonePolygon } from './ZonePolygon';

const noop = () => undefined;
const MAX_CANVAS_HEIGHT_RATIO = 0.7;

interface Props {
  imageUrl?: string;
  zones: ZonePolygonData[];
  selectedZoneId?: string | null;
  onZoneChange?: (zone: ZonePolygonData) => void;
  onSelectZone?: (zoneId: string | null) => void;
  onCanvasDimensionsChange?: (dimensions: CanvasDimensions) => void;
  onImageResolutionChange?: (resolution: ImageResolution) => void;
  canUpdateDeadZones?: boolean;
  canUpdateVehicleParkingZone?: boolean;
  canUpdateActiveZone?: boolean;
  canUpdatePresenceZone?: boolean;
  externalDimensions?: CanvasDimensions;
  externalImageResolution?: ImageResolution;
  readOnly?: boolean;
}

interface ReadOnlyProps {
  zones: ZonePolygonData[];
  dimensions: CanvasDimensions;
  imageResolution: ImageResolution;
}

interface EditableProps {
  imageUrl: string;
  zones: ZonePolygonData[];
  selectedZoneId: string | null;
  onZoneChange: (zone: ZonePolygonData) => void;
  onSelectZone: (zoneId: string | null) => void;
  onCanvasDimensionsChange: (dimensions: CanvasDimensions) => void;
  onImageResolutionChange: (resolution: ImageResolution) => void;
  canUpdateDeadZones: boolean;
  canUpdateVehicleParkingZone: boolean;
  canUpdateActiveZone: boolean;
  canUpdatePresenceZone: boolean;
}

function isReadOnlyCanvas(
  props: Props
): props is Props &
  Required<Pick<Props, 'externalDimensions' | 'externalImageResolution'>> {
  return Boolean(
    props.readOnly && props.externalDimensions && props.externalImageResolution
  );
}

const ReadOnlyZoneCanvas = memo(function ReadOnlyZoneCanvas({
  zones,
  dimensions,
  imageResolution,
}: ReadOnlyProps) {
  const displayedZones = rescaleZones(zones, imageResolution, dimensions);

  return (
    <div className="h-full w-full">
      <Stage width={dimensions.width} height={dimensions.height}>
        <Layer>
          {displayedZones.map((zone) => (
            <ZonePolygon
              key={zone.id}
              zoneId={zone.id}
              points={zone.points}
              bounds={dimensions}
              onPointsChange={noop}
              type={zone.type}
              label={zone.label}
              isSelected={false}
              onSelect={noop}
              canEdit={false}
            />
          ))}
        </Layer>
      </Stage>
    </div>
  );
});

const EditableZoneCanvas = memo(function EditableZoneCanvas({
  imageUrl,
  zones,
  selectedZoneId,
  onZoneChange,
  onSelectZone,
  onCanvasDimensionsChange,
  onImageResolutionChange,
  canUpdateDeadZones,
  canUpdateVehicleParkingZone,
  canUpdateActiveZone,
  canUpdatePresenceZone,
}: EditableProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { image, loadState } = useImageLoader(imageUrl);
  const [dimensions, setDimensions] = useState<CanvasDimensions>({
    width: 0,
    height: 0,
  });

  // Keep image ref updated for stable access in ResizeObserver
  imageRef.current = image ?? null;

  const updateDimensions = useCallback(() => {
    const img = imageRef.current;
    if (!containerRef.current || !img) return;
    const containerWidth = containerRef.current.offsetWidth;
    if (containerWidth === 0) return;
    const maxHeight = window.innerHeight * MAX_CANVAS_HEIGHT_RATIO;
    const scale = Math.min(
      containerWidth / img.naturalWidth,
      maxHeight / img.naturalHeight
    );
    const newDimensions = {
      width: img.naturalWidth * scale,
      height: img.naturalHeight * scale,
    };
    setDimensions(newDimensions);
    onCanvasDimensionsChange(newDimensions);
    onImageResolutionChange({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  }, [onCanvasDimensionsChange, onImageResolutionChange]);

  // Update dimensions when image loads
  useEffect(() => {
    if (image) updateDimensions();
  }, [image, updateDimensions]);

  // ResizeObserver - always observes the same container element
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;
    const scheduleUpdate = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => updateDimensions());
    };
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(container);
    // Height-only viewport resizes leave the container width untouched, so the
    // observer alone would keep a stale height cap.
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [updateDimensions]);

  const handlePointsChange = useCallback(
    (zoneId: string, points: Point[]) => {
      const zone = zones.find((z) => z.id === zoneId);
      if (zone) {
        onZoneChange({ ...zone, points });
      }
    },
    [zones, onZoneChange]
  );

  const handleSelectZone = useCallback(
    (zoneId: string) => onSelectZone(zoneId),
    [onSelectZone]
  );

  const canEditZone = useCallback(
    (zoneType: SpecialZoneType) => {
      if (zoneType === SpecialZoneType.DeadZone) return canUpdateDeadZones;
      if (zoneType === SpecialZoneType.VehicleParkingZone)
        return canUpdateVehicleParkingZone;
      if (zoneType === SpecialZoneType.ActiveZone) return canUpdateActiveZone;
      if (zoneType === SpecialZoneType.PresenceZone)
        return canUpdatePresenceZone;
      return false;
    },
    [
      canUpdateDeadZones,
      canUpdateVehicleParkingZone,
      canUpdateActiveZone,
      canUpdatePresenceZone,
    ]
  );

  const isEmptyUrl = !imageUrl || imageUrl.trim() === '';
  const isLoading = loadState === 'loading' || !image || dimensions.width === 0;
  const isError = loadState === 'error';
  const showCanvas = !isEmptyUrl && !isLoading && !isError;

  // Single container div - ResizeObserver always observes this element
  return (
    <div ref={containerRef} className="w-full">
      {isEmptyUrl && (
        <div className="bg-ondark-bg-2 flex h-64 flex-col items-center justify-center gap-4 rounded-lg">
          <span className="text-ondark-text-2">Invalid image URL.</span>
        </div>
      )}
      {!isEmptyUrl && isError && (
        <div className="bg-ondark-bg-2 flex h-64 flex-col items-center justify-center gap-4 rounded-lg">
          <span className="text-ondark-text-2">
            Image failed to load. The URL may have expired.
          </span>
          <Button variant="primary" onClick={() => window.location.reload()}>
            <ArrowPathIcon className="mr-2 h-4 w-4" />
            Refresh Page
          </Button>
        </div>
      )}
      {!isEmptyUrl && isLoading && !isError && (
        <div className="bg-ondark-bg-2 flex h-64 items-center justify-center rounded-lg">
          <span className="text-ondark-text-2">Loading image...</span>
        </div>
      )}
      {showCanvas && (
        <div className="flex justify-center">
          <Stage width={dimensions.width} height={dimensions.height}>
            <Layer>
              <KonvaImage
                image={image}
                width={dimensions.width}
                height={dimensions.height}
              />
            </Layer>
            <Layer>
              {zones.map((zone) => (
                <ZonePolygon
                  key={zone.id}
                  zoneId={zone.id}
                  points={zone.points}
                  bounds={dimensions}
                  onPointsChange={handlePointsChange}
                  type={zone.type}
                  label={zone.label}
                  isSelected={selectedZoneId === zone.id}
                  onSelect={handleSelectZone}
                  canEdit={canEditZone(zone.type)}
                />
              ))}
            </Layer>
          </Stage>
        </div>
      )}
    </div>
  );
});

export const ZoneCanvas = memo(function ZoneCanvas(props: Props) {
  if (isReadOnlyCanvas(props)) {
    return (
      <ReadOnlyZoneCanvas
        zones={props.zones}
        dimensions={props.externalDimensions}
        imageResolution={props.externalImageResolution}
      />
    );
  }

  return (
    <EditableZoneCanvas
      imageUrl={props.imageUrl ?? ''}
      zones={props.zones}
      selectedZoneId={props.selectedZoneId ?? null}
      onZoneChange={props.onZoneChange ?? noop}
      onSelectZone={props.onSelectZone ?? noop}
      onCanvasDimensionsChange={props.onCanvasDimensionsChange ?? noop}
      onImageResolutionChange={props.onImageResolutionChange ?? noop}
      canUpdateDeadZones={props.canUpdateDeadZones ?? false}
      canUpdateVehicleParkingZone={props.canUpdateVehicleParkingZone ?? false}
      canUpdateActiveZone={props.canUpdateActiveZone ?? false}
      canUpdatePresenceZone={props.canUpdatePresenceZone ?? false}
    />
  );
});

export default ZoneCanvas;
